/**
 * EvalsListPage (`/evals`) — top level of the read-only eval viewer.
 *
 * Header stat row (groups / tasks / runs / overall score) and a card
 * per eval group: score, trend, and every member task's headline score
 * as a chip — the overview answers "how is each task doing" without a
 * click. Runs are pushed by external pipelines — there is no in-UI
 * authoring, so the empty state points at the ingestion API.
 */

import { FlaskConical } from 'lucide-react'
import { Link } from 'react-router-dom'

import { getApiErrorDetail } from '@/api/client'
import { BurndownSparkline } from '@/components/benchmarks/BurndownSparkline'
import { AddBenchmarkButton } from '@/components/evals/AddBenchmarkDialog'
import { RenameDialog } from '@/components/shared/RenameDialog'
import { loadSeriesSelection } from '@/components/benchmarks/benchmarkAnalysis'
import { formatRelative, formatScore } from '@/components/evals/format'
import { groupTaskCountLabel } from '@/components/evals/groupScore'
import { isNewTask } from '@/components/evals/newTask'
import { HistoryPlot } from '@/components/evals/ScoreHistoryChart'
import { toChartData } from '@/components/evals/climb'
import { byRecentActivity } from '@/components/evals/groupOrder'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { LoadingPanel } from '@/components/shared/LoadingScreen'
import { useMinimumLoading } from '@/hooks/useMinimumSplash'
import {
  useBenchmarksList,
  useRenameBenchmark,
} from '@/hooks/useBenchmarks'
import { useEvalsOverview, useRenameEvalGroup } from '@/hooks/useEvals'
import { PUBLIC_API_BASE } from '@/lib/env'
import type { BenchmarkListItem, EvalGroupSummary } from '@/api/generated/model'

// Point at whichever API this deployment ingests through, so the snippet is
// copy-pasteable locally (localhost:8000) as well as in test/prod.
const CURL_SNIPPET = `curl -X POST ${PUBLIC_API_BASE}/v1/eval-groups \\
  -H "Authorization: Bearer $TRAINLOOP_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"slug": "my-benchmarks", "display_name": "My Benchmarks"}'`

/**
 * Card for an eval with failure analysis behind it (bucket-backed corpus:
 * per-task rubrics, per-model scores, rollout trajectories). Renders
 * exactly like a pushed eval group's card — score chip, meta line, chart —
 * the chart is just the task-score burndown instead of a time series.
 * (Future third kind: online evals monitoring agent traffic.)
 */
function BenchmarkCard({ benchmark }: { benchmark: BenchmarkListItem }): React.JSX.Element {
  const rename = useRenameBenchmark()
  const onRename = (name: string): void => {
    rename.mutate({ benchmarkSlug: benchmark.slug, data: { display_name: name } })
  }
  // TRA-922: a self-serve ingestion that hasn't completed renders as a
  // stub card — no link, no sparkline, just its state (and error).
  if (benchmark.status !== 'ready') {
    // Same skeleton as the live card — title row with a chip where the
    // score sits, meta line, then a 2:1 chart area. The chart-to-be is a
    // dashed hairline with no fill (the empty-state idiom), so the card
    // occupies the same footprint as its siblings.
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between gap-2 text-base">
            <span className="group/rename flex min-w-0 items-center gap-1">
              <span className="truncate">{benchmark.display_name}</span>
              <RenameDialog
                title="Rename benchmark"
                slug={benchmark.slug}
                displayName={benchmark.display_name}
                maxLength={255}
                onSave={onRename}
              />
            </span>
            {benchmark.status === 'processing' ? (
              <Badge variant="secondary">Processing</Badge>
            ) : (
              <Badge variant="destructive">Failed</Badge>
            )}
          </CardTitle>
          <p className="break-all text-xs text-muted-foreground">
            {benchmark.description}
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex aspect-[2/1] w-full flex-col items-center justify-center gap-3 border border-dashed border-border p-6">
            {benchmark.status === 'processing' ? (
              <>
                <img
                  src="/TrainLoop_Loading_Black.svg"
                  alt=""
                  aria-hidden="true"
                  className="h-8 w-8"
                />
                <p className="max-w-sm text-center text-sm text-muted-foreground">
                  Running failure and loss analysis. This can take a few days.
                </p>
              </>
            ) : (
              <p className="max-w-sm break-words text-center text-sm text-destructive">
                {benchmark.error}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    )
  }
  // Mirror the benchmark page's series toggle — the card plots the same
  // statistic(s), and the chip averages the first selected series.
  const series = loadSeriesSelection(benchmark.slug)
  const primary = series[0] ?? 'best'
  const overall =
    benchmark.task_scores.length > 0
      ? benchmark.task_scores.reduce((sum, point) => sum + point[primary], 0) /
        benchmark.task_scores.length
      : null
  return (
    <Link
      to={`/evals/benchmarks/${benchmark.slug}`}
      className="block focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <Card className="h-full transition-colors hover:border-primary/50">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between gap-2 text-base">
            <span className="group/rename flex min-w-0 items-center gap-1">
              <span className="truncate">{benchmark.display_name}</span>
              <RenameDialog
                title="Rename benchmark"
                slug={benchmark.slug}
                displayName={benchmark.display_name}
                maxLength={255}
                onSave={onRename}
              />
            </span>
            <span className="rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-sm tabular-nums">
              {formatScore(overall)}
            </span>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {benchmark.task_count} tasks · {benchmark.models.length}{' '}
            {benchmark.models.length === 1 ? 'model' : 'models'} · updated{' '}
            {formatRelative(benchmark.generated_at)}
          </p>
        </CardHeader>
        <CardContent>
          <BurndownSparkline curve={benchmark.task_scores} series={series} className="w-full" />
        </CardContent>
      </Card>
    </Link>
  )
}

/** Pencil + dialog for a group card title (TRA-947). */
function GroupRenamePencil({ group }: { group: EvalGroupSummary }): React.JSX.Element {
  const { rename } = useRenameEvalGroup(group.slug)
  return (
    <RenameDialog
      title="Rename eval group"
      slug={group.slug}
      displayName={group.display_name}
      maxLength={120}
      onSave={rename}
    />
  )
}

/**
 * Small GitHub marker for a repo-synced group ("github:<org>/<repo>" source).
 * The repo name in the title attribute answers "synced from where?".
 */
/** "Aug 4, 2026 @ 9:45 PM PDT" — sync times read in local office time. */
function formatSyncedAt(iso: string): string {
  const date = new Date(iso)
  const day = date.toLocaleDateString('en-US', {
    timeZone: 'America/Los_Angeles',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const time = date.toLocaleTimeString('en-US', {
    timeZone: 'America/Los_Angeles',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
  return `${day} @ ${time}`
}

function RepoSourceBadge({
  source,
  syncedAt,
  className = 'size-3.5',
}: {
  source: string
  /** When set, the hover text adds "last synced <time>". */
  syncedAt?: string
  className?: string
}): React.JSX.Element | null {
  if (!source.startsWith('github:')) return null
  const repo = source.slice('github:'.length)
  const label =
    syncedAt === undefined
      ? `Synced from ${repo}`
      : `Synced from ${repo} · last synced ${formatSyncedAt(syncedAt)}`
  return (
    // Radix tooltip (instant) rather than the browser's slow native title.
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Inline GitHub mark — lucide's brand icons are deprecated. */}
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          role="img"
          aria-label={label}
          className={`shrink-0 text-muted-foreground ${className}`}
        >
          <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
        </svg>
      </TooltipTrigger>
      {/* font-sans + text-xs: match the chart tooltips, not the serif titles
          the portal would otherwise inherit. */}
      <TooltipContent className="max-w-md font-sans text-xs font-normal">
        <p className="text-muted-foreground">{label}</p>
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * Compact card for a group with no runs — proof its definitions landed
 * (synced from the customer repo or pushed) without a tall empty chart
 * slot. Graduates to the full GroupCard on the first pushed run.
 */
function SyncedGroupCard({ group }: { group: EvalGroupSummary }): React.JSX.Element {
  const newTasks = group.tasks.filter(
    (task) => task.status === 'active' && isNewTask(task.created_at),
  ).length
  const draftTasks = group.tasks.filter((task) => task.status === 'draft').length
  // Drafts aren't synced yet — they're incoming. A group whose only tasks
  // are drafts says so instead of claiming a synced task it doesn't have.
  const syncedTasks = group.task_count - draftTasks
  const countLabel =
    syncedTasks > 0
      ? `${String(syncedTasks)} ${syncedTasks === 1 ? 'task' : 'tasks'} synced`
      : `${String(draftTasks)} draft ${draftTasks === 1 ? 'task' : 'tasks'} incoming`
  return (
    <Link
      to={`/evals/groups/${group.slug}`}
      className="block focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <Card className="transition-colors hover:border-primary/50">
        <CardHeader className="pb-2">
          <CardTitle className="group/rename flex items-center gap-2 text-base">
            <span className="truncate">{group.display_name}</span>
            <GroupRenamePencil group={group} />
            {/* Right side: the GitHub mark IS the status for sourced groups
                (hover shows repo + last synced); plain chip otherwise. */}
            {group.source.startsWith('github:') ? (
              <span className="ml-auto">
                <RepoSourceBadge
                  source={group.source}
                  syncedAt={group.updated_at}
                  className="size-4"
                />
              </span>
            ) : (
              <span className="ml-auto rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {group.task_count > 0 ? 'synced' : 'empty'}
              </span>
            )}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {group.task_count > 0 ? (
              <>
                {/* Hovering the count pops the task list — inline chips per
                    card make the whole list unreadable. */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="underline decoration-dotted underline-offset-2">
                      {countLabel}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-md font-sans text-xs font-normal">
                    <div className="flex max-h-64 flex-wrap gap-1 overflow-y-auto">
                      {group.tasks.map((task) => (
                        <span
                          key={task.slug}
                          className="rounded border border-border/50 px-1.5 py-0.5 text-xs"
                        >
                          {task.display_name}
                          {task.status === 'draft' ? (
                            <span className="text-muted-foreground"> · draft</span>
                          ) : (
                            isNewTask(task.created_at) && (
                              <span className="text-muted-foreground"> · new</span>
                            )
                          )}
                        </span>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
                {newTasks > 0 && (
                  <span className="text-foreground/70"> · {newTasks} added this week</span>
                )}
                {syncedTasks > 0 && draftTasks > 0 && (
                  <span className="text-foreground/70">
                    {' '}· {draftTasks} draft{draftTasks === 1 ? '' : 's'} incoming
                  </span>
                )}{' '}
                · awaiting first run · added {formatRelative(group.created_at)}
              </>
            ) : (
              `No tasks yet · added ${formatRelative(group.created_at)}`
            )}
          </p>
        </CardHeader>
      </Card>
    </Link>
  )
}

function GroupCard({ group }: { group: EvalGroupSummary }): React.JSX.Element {
  // Tasks synced from the customer's repo that no run has covered yet —
  // called out on the meta line so a freshly merged test is visibly "in".
  const pendingTasks = group.tasks.filter((task) => task.score === null).length
  // Recent additions get their own callout: "awaiting first run" vanishes
  // at the first run, but a task stays visibly NEW for its whole window.
  const newTasks = group.tasks.filter(
    (task) => task.status === 'active' && isNewTask(task.created_at),
  ).length
  const draftTasks = group.tasks.filter((task) => task.status === 'draft').length
  return (
    <Link to={`/evals/groups/${group.slug}`} className="block focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
    <Card className="h-full transition-colors hover:border-primary/50">
      <CardHeader className="pb-2">
        <CardTitle className="group/rename flex items-center gap-2 text-base">
          <span className="truncate">{group.display_name}</span>
          <RepoSourceBadge source={group.source} syncedAt={group.updated_at} />
          <GroupRenamePencil group={group} />
          <span className="ml-auto rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-sm tabular-nums">
            {formatScore(group.score, group.score_format)}
          </span>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {groupTaskCountLabel(group.task_count, group.splits)} ·{' '}
          {group.run_count > 0 ? (
            <>
              {group.run_count} {group.run_count === 1 ? 'run' : 'runs'} · last run{' '}
              {formatRelative(group.last_run_at)}
            </>
          ) : (
            // Reference-only group: the chart shows levels to beat, and
            // every task is pending, so the per-task pending count below
            // would just restate the task count.
            <>no scored runs yet</>
          )}
          {newTasks > 0 && (
            <span className="text-foreground/70">
              {' '}· {newTasks} added this week
            </span>
          )}
          {draftTasks > 0 && (
            <span className="text-foreground/70">
              {' '}· {draftTasks} draft{draftTasks === 1 ? '' : 's'}
            </span>
          )}
          {group.run_count > 0 && pendingTasks > 0 && (
            <span className="text-foreground/70">
              {' '}· {pendingTasks} awaiting first run
            </span>
          )}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="w-full">
          <HistoryPlot
            data={toChartData(group.history)}
            format={group.score_format}
            referenceLines={group.reference_lines}
          />
        </div>
      </CardContent>
    </Card>
    </Link>
  )
}

export default function EvalsListPage(): React.JSX.Element {
  const { data: overview, isLoading, isError, error } = useEvalsOverview()
  const benchmarks = useBenchmarksList()
  // Hold the loading panel long enough for the animated icon to be seen.
  const loading = useMinimumLoading(isLoading || benchmarks.isLoading)

  const benchmarkCards = benchmarks.data?.benchmarks ?? []
  const hasAnything =
    (overview !== undefined && overview.groups.length > 0) || benchmarkCards.length > 0
  const groups = overview?.groups ?? []
  // Most recently updated first, by the same rule in both grids — the group
  // you just pushed to is the one you came here to look at. Run count is
  // deliberately not part of this: it ranked big old groups over new ones.
  // Reference-only groups get the chart card too: the levels to beat are
  // worth a chart before the first scored run lands (same rule as the
  // group page's score-over-time card).
  const hasChart = (group: EvalGroupSummary): boolean =>
    group.run_count > 0 ||
    (group.reference_lines !== undefined && group.reference_lines.length > 0)
  const scoredGroups = groups.filter(hasChart).sort(byRecentActivity)
  const runlessGroups = groups.filter((group) => !hasChart(group)).sort(byRecentActivity)

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="Evals"
        description="Benchmark corpora and scores pushed by training and eval pipelines."
        actions={<AddBenchmarkButton />}
      />

      {/* One gate: loading until BOTH sources settle, so loaded cards
          never render next to a leftover loading state. */}
      {loading && !isError && !benchmarks.isError && <LoadingPanel />}

      {isError && (
        <p className="text-sm text-destructive">
          Failed to load eval groups: {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}
      {benchmarks.isError && (
        <p className="text-sm text-destructive">
          Failed to load benchmarks: {getApiErrorDetail(benchmarks.error) ?? 'unknown error'}
        </p>
      )}

      {!loading && !isError && !hasAnything && (
        <EmptyState
          icon={FlaskConical}
          title="No evals yet"
          description="Push one via the API to start tracking scores."
          action={
            <pre className="max-w-full overflow-x-auto rounded-md bg-muted p-3 text-left font-mono text-xs text-muted-foreground">
              {CURL_SNIPPET}
            </pre>
          }
        />
      )}

      {!loading && !isError && overview && hasAnything && (
        <>
          {/* Two grids, not one: a chart card is ~5x the height of a
              minimized one, so mixing them leaves a ragged row wherever the
              two kinds meet. Scored groups get the chart grid;
              synced-but-runless groups pack tightly below. Each grid is
              ordered most recently updated first. */}
          <div className="grid gap-4 lg:grid-cols-2">
            {benchmarkCards.map((benchmark) => (
              <BenchmarkCard key={benchmark.slug} benchmark={benchmark} />
            ))}
            {scoredGroups.map((group) => (
              <GroupCard key={group.id} group={group} />
            ))}
          </div>
          {runlessGroups.length > 0 && (
            <div className="grid gap-3 lg:grid-cols-2">
              {runlessGroups.map((group) => (
                <SyncedGroupCard key={group.id} group={group} />
              ))}
            </div>
          )}
        </>
      )}
    </PageContainer>
  )
}
